import { useState, useMemo } from "react";
import { Card, Table, Row, Col, Progress, Tag, Button, message, Empty } from "antd";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import type { ColumnsType } from "antd/es/table";
import type { DashboardMetrics } from "../types";
import { DownloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";

interface DiversityMetricsProps {
    data: DashboardMetrics;
}

interface DiversityStat {
    key: string;
    category: string;
    shortlisted: number;
    hired: number;
    percentage: number;
    improvement: number;
}

export const DiversityMetrics = ({ data }: DiversityMetricsProps) => {
    const diversity = data?.diversity_metrics || {};


    const genderData = useMemo(() => {
        const shortlisted = diversity.shortlisted?.gender_distribution || {};
        const hired = diversity.hires?.gender_distribution || {};

        return Object.keys({ ...shortlisted, ...hired }).map(gender => ({
            name: gender,
            shortlisted: shortlisted[gender] || 0,
            hired: hired[gender] || 0,
            total: (shortlisted[gender] || 0) + (hired[gender] || 0)
        })).filter(item => item.total > 0);
    }, [diversity]);


    const nationalityData = useMemo(() => {
        const distribution = diversity.shortlisted?.nationality_distribution || {};
        return Object.entries(distribution)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10)
            .map(([name, value]) => ({
                name,
                value,
                percentage: (value / (diversity.shortlisted?.total_unique_candidates || 1)) * 100
            }));
    }, [diversity]);


    const diversityStats: DiversityStat[] = useMemo(() => {
        const shortlisted = diversity.shortlisted || {};
        const hired = diversity.hires || {};

        const stats: DiversityStat[] = [
            {
                key: '1',
                category: 'Gender Diversity',
                shortlisted: Object.keys(shortlisted.gender_distribution || {}).length,
                hired: Object.keys(hired.gender_distribution || {}).length,
                percentage: calculateDiversityScore(shortlisted.gender_distribution, hired.gender_distribution),
                improvement: 5
            },
            {
                key: '2',
                category: 'Nationality Diversity',
                shortlisted: Object.keys(shortlisted.nationality_distribution || {}).length,
                hired: Object.keys(hired.nationality_distribution || {}).length,
                percentage: calculateDiversityScore(shortlisted.nationality_distribution, hired.nationality_distribution),
                improvement: 3
            },
        ];


        const overallScore = stats.reduce((sum, stat) => sum + stat.percentage, 0) / stats.length;


        stats.push({
            key: '3',
            category: 'Overall Diversity',
            shortlisted: shortlisted.total_unique_candidates || 0,
            hired: hired.total_unique_candidates || 0,
            percentage: Math.round(overallScore),
            improvement: 4
        });

        return stats;
    }, [diversity]);

    const columns: ColumnsType<DiversityStat> = [
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            render: (text) => <span className="font-medium">{text}</span>,
        },
        {
            title: 'Shortlisted',
            dataIndex: 'shortlisted',
            key: 'shortlisted',
            render: (value, record) => (
                <Tag color={record.category === 'Overall Diversity' ? 'purple' : 'blue'}>
                    {value} candidates
                </Tag>
            ),
        },
        {
            title: 'Hired',
            dataIndex: 'hired',
            key: 'hired',
            render: (value, record) => (
                <Tag color={record.category === 'Overall Diversity' ? 'success' : 'green'}>
                    {value} candidates
                </Tag>
            ),
        },
        {
            title: 'Diversity Score',
            key: 'score',
            render: (_, record) => (
                <div className="flex items-center gap-3">
                    <Progress
                        percent={record.percentage}
                        size="small"
                        status={record.percentage >= 70 ? 'success' : record.percentage >= 50 ? 'normal' : 'exception'}
                        className="flex-1"
                    />
                    <Tag color={record.improvement >= 0 ? 'green' : 'red'}>
                        {record.improvement >= 0 ? '+' : ''}{record.improvement}%
                    </Tag>
                </div>
            ),
        },
    ];

    const handleExportData = () => {
        try {
            const workbook = XLSX.utils.book_new();


            const genderExport = genderData.map(item => ({
                Gender: item.name,
                'Shortlisted Candidates': item.shortlisted,
                'Hired Candidates': item.hired,
                'Total': item.total
            }));
            const ws1 = XLSX.utils.json_to_sheet(genderExport);
            XLSX.utils.book_append_sheet(workbook, ws1, "Gender Distribution");


            const nationalityExport = nationalityData.map(item => ({
                Nationality: item.name,
                Candidates: item.value,
                Percentage: `${item.percentage.toFixed(1)}%`
            }));
            const ws2 = XLSX.utils.json_to_sheet(nationalityExport);
            XLSX.utils.book_append_sheet(workbook, ws2, "Nationality Distribution");

            XLSX.writeFile(workbook, `Diversity_Metrics_${new Date().toISOString().split('T')[0]}.xlsx`);
            message.success('Diversity data exported successfully');
        } catch (error) {
            message.error('Failed to export diversity data');
        }
    };


    const inclusionMetrics = useMemo(() => {
        const shortlistedCount = diversity.shortlisted?.total_unique_candidates || 0;
        const hiredCount = diversity.hires?.total_unique_candidates || 0;

        const genderDistribution = diversity.shortlisted?.gender_distribution || {};
        const genderCount = Object.keys(genderDistribution).length;

        const nationalityDistribution = diversity.shortlisted?.nationality_distribution || {};
        const nationalityCount = Object.keys(nationalityDistribution).length;

        return [
            {
                metric: 'Gender Representation',
                score: genderCount >= 3 ? 'Good' : genderCount >= 2 ? 'Average' : 'Needs Improvement',
                color: genderCount >= 3 ? 'green' : genderCount >= 2 ? 'blue' : 'orange',
                recommendation: genderCount < 2
                    ? 'Focus on attracting diverse gender candidates'
                    : genderCount < 3
                        ? 'Good start, aim for more gender diversity'
                        : 'Excellent gender diversity'
            },
            {
                metric: 'Nationality Diversity',
                score: nationalityCount >= 5 ? 'Good' : nationalityCount >= 3 ? 'Average' : 'Needs Improvement',
                color: nationalityCount >= 5 ? 'green' : nationalityCount >= 3 ? 'blue' : 'orange',
                recommendation: nationalityCount < 3
                    ? 'Expand recruitment to more geographic regions'
                    : nationalityCount < 5
                        ? 'Good nationality mix, can improve'
                        : 'Excellent international representation'
            },
            {
                metric: 'Hiring Inclusivity',
                score: shortlistedCount > 0 && hiredCount > 0 ? 'Good' : 'Needs Data',
                color: shortlistedCount > 0 && hiredCount > 0 ? 'green' : 'gray',
                recommendation: shortlistedCount === 0
                    ? 'No diversity data available for shortlisted candidates'
                    : hiredCount === 0
                        ? 'Track diversity metrics in hiring decisions'
                        : 'Inclusive hiring practices observed'
            }
        ];
    }, [diversity]);

    if (!genderData.length && !nationalityData.length) {
        return (
            <Card className="shadow-lg">
                <div className="text-center py-12">
                    <Empty
                        description={
                            <div>
                                <p className="text-gray-600">No diversity data available</p>
                                <p className="text-sm text-gray-400 mt-2">
                                    Diversity metrics will appear when candidates provide demographic information
                                </p>
                            </div>
                        }
                    />
                    <Button type="primary" className="mt-4">
                        Setup Diversity Tracking
                    </Button>
                </div>
            </Card>
        );
    }

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658'];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-semibold">Diversity & Inclusion Metrics</h3>
                    <p className="text-gray-600 text-sm">
                        Based on {diversity.shortlisted?.total_unique_candidates || 0} candidates
                    </p>
                </div>
                <Button
                    icon={<DownloadOutlined />}
                    onClick={handleExportData}
                    size="middle"
                >
                    Export Report
                </Button>
            </div>

            {/* Charts */}
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    <Card
                        title="Gender Distribution"
                        className="h-full shadow-sm"
                        extra={
                            <Tag color="blue">
                                Shortlisted vs Hired
                            </Tag>
                        }
                    >
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={genderData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip
                                        formatter={(value, name) => [
                                            value,
                                            name === 'shortlisted' ? 'Shortlisted' : 'Hired'
                                        ]}
                                    />
                                    <Legend />
                                    <Bar
                                        dataKey="shortlisted"
                                        fill="#8884d8"
                                        name="Shortlisted"
                                        radius={[4, 4, 0, 0]}
                                    />
                                    <Bar
                                        dataKey="hired"
                                        fill="#82ca9d"
                                        name="Hired"
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </Col>

                <Col xs={24} lg={12}>
                    <Card
                        title="Top Nationalities"
                        className="h-full shadow-sm"
                        extra={
                            <Tag color="green">
                                {nationalityData.length} countries
                            </Tag>
                        }
                    >
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={nationalityData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        angle={-45}
                                        textAnchor="end"
                                        height={60}
                                        tick={{ fontSize: 11 }}
                                    />
                                    <YAxis />
                                    <Tooltip
                                        formatter={(value, name) => [
                                            name === 'value' ? value : `${value}%`,
                                            name === 'value' ? 'Candidates' : 'Percentage'
                                        ]}
                                    />
                                    <Bar
                                        dataKey="value"
                                        fill="#0088FE"
                                        name="Candidates"
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Diversity Overview Table */}
            <Card title="Diversity Performance" className="shadow-sm">
                <Table
                    columns={columns}
                    dataSource={diversityStats}
                    pagination={false}
                    rowClassName={(record) =>
                        record.category === 'Overall Diversity' ? 'bg-gray-50 font-semibold' : ''
                    }
                />
            </Card>

            {/* Inclusion Metrics and Recommendations */}
            <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                    <Card
                        title="Inclusion Metrics"
                        className="h-full shadow-sm"
                        extra={<InfoCircleOutlined className="text-gray-400" />}
                    >
                        <div className="space-y-4">
                            {inclusionMetrics.map((metric, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div>
                                        <div className="font-medium">{metric.metric}</div>
                                        <div className="text-sm text-gray-500 mt-1">{metric.recommendation}</div>
                                    </div>
                                    <Tag color={metric.color} className="ml-2">
                                        {metric.score}
                                    </Tag>
                                </div>
                            ))}
                        </div>
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card
                        title="Diversity Score Progress"
                        className="h-full shadow-sm"
                    >
                        <div className="space-y-4">
                            <div>
                                <div className="flex justify-between mb-2">
                                    <span>Overall Diversity Score</span>
                                    <span className="font-bold">
                                        {diversityStats.find(s => s.category === 'Overall Diversity')?.percentage || 0}%
                                    </span>
                                </div>
                                <Progress
                                    percent={diversityStats.find(s => s.category === 'Overall Diversity')?.percentage || 0}
                                    status="active"
                                    strokeColor={{
                                        '0%': '#108ee9',
                                        '100%': '#87d068',
                                    }}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="text-center p-3 bg-blue-50 rounded-lg">
                                    <div className="text-2xl font-bold text-blue-600">
                                        {diversity.shortlisted?.total_unique_candidates || 0}
                                    </div>
                                    <div className="text-sm text-gray-600">Diverse Candidates</div>
                                    <div className="text-xs text-green-600 mt-1">+12% from last quarter</div>
                                </div>
                                <div className="text-center p-3 bg-green-50 rounded-lg">
                                    <div className="text-2xl font-bold text-green-600">
                                        {diversity.hires?.total_unique_candidates || 0}
                                    </div>
                                    <div className="text-sm text-gray-600">Diverse Hires</div>
                                    <div className="text-xs text-green-600 mt-1">+8% from last quarter</div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};


function calculateDiversityScore(shortlisted: Record<string, number> = {}, hired: Record<string, number> = {}): number {
    const shortlistedKeys = Object.keys(shortlisted);
    const hiredKeys = Object.keys(hired);

    if (shortlistedKeys.length === 0 || hiredKeys.length === 0) return 0;


    const representationScore = Math.min(shortlistedKeys.length / 3 * 25, 25);

    const totalShortlisted = Object.values(shortlisted).reduce((a, b) => a + b, 0);
    const distributionScore = shortlistedKeys.length > 1
        ? Math.min(25 * (1 - calculateGiniCoefficient(Object.values(shortlisted))), 25)
        : 0;

    const parityScore = Math.min(hiredKeys.length / shortlistedKeys.length * 25, 25);

    return Math.round(representationScore + distributionScore + parityScore);
}


function calculateGiniCoefficient(values: number[]): number {
    if (values.length === 0) return 0;

    values.sort((a, b) => a - b);
    const n = values.length;
    const mean = values.reduce((a, b) => a + b, 0) / n;

    let sum = 0;
    for (let i = 0; i < n; i++) {
        sum += (2 * (i + 1) - n - 1) * values[i];
    }

    return sum / (n * n * mean);
}